import { Button, Card, Col, Form, Row } from "antd";
import FullPageLayout from "../../../templates/FullPageLayout";
import UploadPhoto from "../../../atoms/UploadPhoto";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { InputField, InputPhone, SelectField } from "../../../atoms/FormFields";
import { useNavigate } from "react-router-dom";
import { createUser, uploadUserImage } from "../ducks/services";
import { AxiosError, AxiosResponse } from "axios";
import { SuccessMessage } from "../../../atoms/Toasts/SuccessMessage";
import { ErrorMessage } from "../../../atoms/Toasts/ErrorMessage";
import { RcFile } from "antd/es/upload";

interface FormValues {
  first_name: string;
  last_name: string;
  phone: string;
  phone_office: string;
  email: string;
  position_id: string;
}

const roleOptions = [
  { label: "Super admin", value: "super_admin" },
  { label: "Client manager", value: "client_manager" },
];

export default function AddTeamMember() {
  const {
    control,
    handleSubmit,
    getValues,
    trigger,
    reset,
    formState: { errors },
  } = useForm<FormValues>();

  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [checklistFile, setChecklistFile] = useState<File | null>(null);
  const [uploadModal, setUploadModal] = useState<boolean>(false);

  const [previewImage, setPreviewImage] = useState<{
    file: string;
    imagePreviewUrl: string | null;
  }>({ file: "", imagePreviewUrl: null });
  const [modalPreviewImage, setModalPreviewImage] = useState<{
    file: string;
    imagePreviewUrl: string | null;
  }>({ file: "", imagePreviewUrl: null });

  const saveUpload = () => {
    setLoading(true);
    setPreviewImage({ ...modalPreviewImage });
    setLoading(false);
    setUploadModal(false);
  };

  const onSubmit = (val: FormValues) => {
    setLoading(true);
    const payload = {
      first_name: val.first_name,
      last_name: val.last_name,
      email: val.email,
      type: val.position_id,
      phone: val.phone,
      phone_office: val?.phone_office ? val?.phone_office : undefined,
      position_id: "",
    };

    createUser(payload)
      .then((res: AxiosResponse) => {
        if (checklistFile && res && res?.data?.id) {
          uploadUserImage({
            owner_id: res?.data?.id,
            image: checklistFile as File | RcFile,
            image_type: "user_image",
          })
            .then((res) => {
              if (res) {
                setLoading(false);
                SuccessMessage("Team member added");
                navigate("/admin-team");
              }
            })
            .catch((e: unknown) => {
              if (e instanceof AxiosError) {
                setLoading(false);
                ErrorMessage(
                  e?.response?.data?.error?.message ?? "Something went wrong"
                );
              } else {
                setLoading(false);
                ErrorMessage("Something went wrong");
              }
            });
        } else {
          setLoading(false);
          SuccessMessage("Team member added");
          navigate("/admin-team");
        }
      })
      .catch((e: unknown) => {
        if (e instanceof AxiosError) {
          setLoading(false);
          ErrorMessage(
            e?.response?.data?.error?.message ?? "Something went wrong"
          );
        } else {
          setLoading(false);
          ErrorMessage("Something went wrong");
        }
      });
  };

  const saveAddMore = async () => {
    const allValues = getValues();
    const isValid = await trigger();

    if (isValid) {
      setLoading(true);

      const payload = {
        first_name: allValues.first_name,
        last_name: allValues.last_name,
        email: allValues.email,
        type: allValues.position_id,
        phone: allValues.phone,
        phone_office: allValues?.phone_office
          ? allValues?.phone_office
          : undefined,
        position_id: "",
      };

      createUser(payload)
        .then((res: AxiosResponse) => {
          if (checklistFile && res && res?.data?.id) {
            uploadUserImage({
              owner_id: res?.data?.id,
              image: checklistFile as File | RcFile,
              image_type: "user_image",
            })
              .then((res) => {
                if (res) {
                  setLoading(false);
                  SuccessMessage("Team member added");
                  window.location.reload();
                  reset();
                }
              })
              .catch((e: unknown) => {
                if (e instanceof AxiosError) {
                  setLoading(false);
                  ErrorMessage(
                    e?.response?.data?.error?.message ?? "Something went wrong"
                  );
                } else {
                  setLoading(false);
                  ErrorMessage("Something went wrong");
                }
              });
          } else {
            setLoading(false);
            SuccessMessage("Team member added");
            window.location.reload();
            reset();
          }
        })
        .catch((e: unknown) => {
          if (e instanceof AxiosError) {
            setLoading(false);
            ErrorMessage(
              e?.response?.data?.error?.message ?? "Something went wrong"
            );
          } else {
            setLoading(false);
            ErrorMessage("Something went wrong");
          }
        });
    }
  };

  return (
    <FullPageLayout loading={loading}>
      <Form
        layout="vertical"
        onFinish={handleSubmit(onSubmit)}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.preventDefault();
        }}
      >
        <Col className="addTeamMember">
          <div className="title">
            <h2> Add team member </h2>
            <p>
              Enter the details below to invite a team member. <br />
              They will receive an invitation to join LandScaling via email.
            </p>
          </div>
          <Card>
            <Row gutter={20}>
              <Col span={24}>
                <UploadPhoto
                  saveUpload={saveUpload}
                  loading={loading}
                  uploadModal={uploadModal}
                  setUploadModal={setUploadModal}
                  onImageDelete={() => null}
                  previewImage={previewImage}
                  checklistFile={checklistFile}
                  modalPreviewImage={modalPreviewImage}
                  setChecklistFile={setChecklistFile}
                  setPreviewImage={setPreviewImage}
                  setModalPreviewImage={setModalPreviewImage}
                  infoTextUploader="A photo helps others recognize this employee easily. It’s optional."
                  //   setIsCropModalOpen={setIsCropModalOpen}
                />
              </Col>

              <Col span={12}>
                <InputField
                  fieldName="first_name"
                  label="First name"
                  control={control}
                  defaultValue=""
                  iProps={{
                    placeholder: "Enter first name",
                    maxLength: 100,
                  }}
                  isRequired
                  rules={{
                    required: "First name is required.",
                  }}
                  validate={errors.first_name && "error"}
                  validMessage={errors.first_name && errors.first_name.message}
                />
              </Col>
              <Col span={12}>
                <InputField
                  fieldName="last_name"
                  label="Last name"
                  control={control}
                  defaultValue=""
                  iProps={{
                    placeholder: "Enter last name",
                    maxLength: 100,
                  }}
                  isRequired
                  rules={{
                    required: "Last name is required.",
                  }}
                  validate={errors.last_name && "error"}
                  validMessage={errors.last_name && errors.last_name.message}
                />
              </Col>
              <Col span={12}>
                <InputPhone
                  fieldName="phone"
                  label="Mobile phone"
                  control={control}
                  isRequired
                  rules={{
                    required: "Mobile phone is required.",
                  }}
                  validate={errors.phone && "error"}
                  validMessage={errors.phone && errors.phone.message}
                />
              </Col>
              <Col span={12}>
                <InputPhone
                  fieldName="phone_office"
                  label="Office phone"
                  control={control}
                />
              </Col>
              <Col span={24}>
                <InputField
                  fieldName="email"
                  label="Account email"
                  control={control}
                  defaultValue=""
                  iProps={{
                    placeholder: "Enter email",
                    maxLength: 100,
                  }}
                  isRequired
                  rules={{
                    required: "Account email is required.",
                    pattern: {
                      value:
                        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                      message: "Please enter a valid email address.",
                    },
                  }}
                  validate={errors.email && "error"}
                  validMessage={errors.email && errors.email.message}
                  tooltipTitle="This email will be used to create the user’s account in LandScaling as well as for all communications with customers."
                />
              </Col>
              <Col span={24} className="mb-0">
                <SelectField
                  fieldName="position_id"
                  label="Role"
                  control={control}
                  selectOption={roleOptions}
                  iProps={{ placeholder: "Select role" }}
                  isRequired
                  rules={{
                    required: "Role is required.",
                  }}
                  validate={errors.position_id && "error"}
                  validMessage={
                    errors.position_id && errors.position_id.message
                  }
                />
              </Col>
            </Row>
          </Card>
        </Col>
        <div className="fixed_btm_card">
          <Card>
            <div className="centered">
              <Button
                htmlType="button"
                className="outlined-btn"
                onClick={() => navigate("/admin-team")}
              >
                Cancel
              </Button>
              <div style={{ display: "flex", gap: "16px" }}>
                <Button
                  htmlType="button"
                  className="outlined-btn"
                  type="primary"
                  onClick={saveAddMore}
                >
                  Save & add one more
                </Button>
                <Button
                  htmlType="submit"
                  type="primary"
                  className="mint-btn"
                  // disabled={true}
                >
                  Save & close
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </Form>
    </FullPageLayout>
  );
}
